import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  CalendarClock,
  Clock,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  Sparkles,
  UserRound,
  Wrench,
} from 'lucide-react';
import type { AdminContactDetail } from '@oracord/shared';
import { apiFetch, ApiError } from '../lib/api';
import { useSessionStore } from '../stores/session';
import { MetricCard, PageHeader, SectionCard, StatusBadge } from '../components/WebPresentation';
import { buildContactCoachingPath } from '../lib/contact-paths';

// Contact detail shows only stable contact facts and links.

export default function ContactDetail() {
  const { phone } = useParams<{ phone: string }>();
  const activeOrgId = useSessionStore(s => s.activeOrganizationId);
  const [data, setData] = useState<AdminContactDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!phone || !activeOrgId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<AdminContactDetail>(
        // React Router already decodes :phone — re-encode for the
        // server path so "+" doesn't get parsed as a space.
        `/api/admin/contacts/${encodeURIComponent(phone)}`,
      );
      setData(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setError('No record of this contact in this organization.');
      } else {
        setError(
          err instanceof ApiError ? err.message : (err as Error).message || 'Failed to load contact',
        );
      }
    } finally {
      setLoading(false);
    }
  }, [phone, activeOrgId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!activeOrgId) {
    return <div className="text-sm text-slate-500">No organization selected.</div>;
  }

  return (
    <div className="max-w-6xl space-y-5">
      <Link
        to="/contacts"
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Back to contacts
      </Link>

      {loading ? (
        <SectionCard className="px-5 py-12 text-center">
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-900">Loading contact…</p>
          <p className="mt-1 text-xs text-slate-500">Pulling only the contact facts this organization already owns.</p>
        </SectionCard>
      ) : error ? (
        <SectionCard className="border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </SectionCard>
      ) : data ? (
        <ContactBody data={data} onRefresh={load} loading={loading} />
      ) : null}
    </div>
  );
}

function ContactBody({
  data,
  onRefresh,
  loading,
}: {
  data: AdminContactDetail;
  onRefresh: () => void;
  loading: boolean;
}) {
  const { contact, stats } = data;
  const displayName = contact.name ?? 'Unknown contact';

  return (
    <>
      <PageHeader
        eyebrow="Contact communication record"
        title={displayName}
        description={(
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono">{contact.phone}</span>
            {contact.company && (
              <>
                <span aria-hidden="true">•</span>
                <span>{contact.company}</span>
              </>
            )}
            <span aria-hidden="true">•</span>
            <span>Contact facts, call counts, and Engine-backed call records only.</span>
          </span>
        )}
        action={(
          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            {stats.pendingFollowUps > 0 ? (
              <StatusBadge tone="amber">{stats.pendingFollowUps} open follow-up{stats.pendingFollowUps === 1 ? '' : 's'}</StatusBadge>
            ) : (
              <StatusBadge tone="slate">No open follow-ups</StatusBadge>
            )}
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm shadow-slate-200/70 transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        )}
      />

      <SectionCard className="overflow-hidden p-0">
        <div className="grid gap-0 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)]">
          <div className="border-b border-slate-100 p-5 lg:border-b-0 lg:border-r">
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                <UserRound className="h-7 w-7" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className={`text-xl font-bold text-slate-950 ${contact.name ? '' : 'italic text-slate-500'}`}>
                    {displayName}
                  </h2>
                </div>
                <p className="mt-1 font-mono text-sm text-slate-600">{contact.phone}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-2 bg-slate-50/70 p-4 sm:grid-cols-3 lg:grid-cols-1">
            {stats.coachingCount > 0 && (
              <ContactContextLink
                to={buildContactCoachingPath(contact.phone)}
                label="Coaching"
                detail={`${stats.coachingCount} coached ${stats.coachingCount === 1 ? 'call' : 'calls'}`}
                tone="purple"
              />
            )}
          </div>
        </div>
      </SectionCard>

      {/* Aggregates derived server-side. */}
      <section className="grid gap-3 md:grid-cols-4">
        <MetricCard
          icon={<Phone className="h-5 w-5 text-blue-600" />}
          label="Calls"
          value={stats.totalCalls}
          tone="blue"
        />
        <MetricCard
          icon={<CalendarClock className="h-5 w-5 text-slate-600" />}
          label="Last contact"
          value={stats.lastContactAt ? new Date(stats.lastContactAt).toLocaleDateString() : '—'}
          tone="slate"
        />
        <MetricCard
          icon={<Clock className="h-5 w-5 text-amber-600" />}
          label="Open follow-ups"
          value={stats.pendingFollowUps}
          tone={stats.pendingFollowUps > 0 ? 'amber' : 'slate'}
        />
        <MetricCard
          icon={<Sparkles className="h-5 w-5 text-purple-600" />}
          label="Coached calls"
          value={stats.coachingCount}
          tone="purple"
        />
      </section>

      {/* Phase 3G — honest coaching rollup. Shown only when at least
          one call on this contact has coaching; no placeholder card
          when absent. `lastCoachingAt` is the coaching row's
          updated_at, not a synthesized metric. */}
      {stats.coachingCount > 0 && (
        <SectionCard className="p-4">
          <Link
            to={buildContactCoachingPath(contact.phone)}
            className="flex flex-col gap-3 rounded-xl bg-purple-50/80 p-4 text-sm text-purple-900 transition hover:bg-purple-100 sm:flex-row sm:items-center sm:justify-between"
            title="Open coaching history filtered to this contact"
          >
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <span>
                <span className="font-semibold">{stats.coachingCount}</span>{' '}
                with coaching context from {stats.coachingCount === 1 ? 'call' : 'calls'}
                {stats.lastCoachingAt && (
                  <>
                    {' · last '}
                    {new Date(stats.lastCoachingAt).toLocaleDateString()}
                  </>
                )}
              </span>
            </span>
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-purple-600">View coaching →</span>
          </Link>
        </SectionCard>
      )}

      {/* Contact metadata. Rendered only when at least one field
          is present — an empty address-book row isn't worth a card. */}
      {(contact.company || contact.email || contact.address || contact.serviceRequested) && (
        <SectionCard className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Saved contact context</div>
              <h2 className="mt-1 text-lg font-bold text-slate-950">Contact details</h2>
            </div>
            <StatusBadge tone="slate" size="sm">Address book facts</StatusBadge>
          </div>
          <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            {contact.company && (
              <Meta icon={<Building2 className="h-4 w-4" />} label="Company" value={contact.company} />
            )}
            {contact.email && (
              <Meta icon={<Mail className="h-4 w-4" />} label="Email" value={contact.email} />
            )}
            {contact.address && (
              <Meta icon={<MapPin className="h-4 w-4" />} label="Address" value={contact.address} />
            )}
            {contact.serviceRequested && (
              <Meta icon={<Wrench className="h-4 w-4" />} label="Service" value={contact.serviceRequested} />
            )}
          </dl>
        </SectionCard>
      )}

    </>
  );
}

function ContactContextLink({
  to,
  label,
  detail,
  tone = 'blue',
}: {
  to: string;
  label: string;
  detail: string;
  tone?: 'blue' | 'amber' | 'slate' | 'purple';
}) {
  const tones = {
    blue: 'border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100',
    amber: 'border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100',
    slate: 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100',
    purple: 'border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100',
  };
  return (
    <Link
      to={to}
      className={`flex min-h-full flex-col justify-between rounded-2xl border px-4 py-3 text-left shadow-sm transition ${tones[tone]}`}
    >
      <span className="text-xs font-semibold uppercase tracking-[0.14em] opacity-75">{label}</span>
      <span className="mt-1 text-sm font-semibold">{detail} →</span>
    </Link>
  );
}

function Meta({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-3">
      <dt className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
        <span className="text-slate-400">{icon}</span>
        {label}
      </dt>
      <dd className="mt-1 break-words text-slate-950">{value}</dd>
    </div>
  );
}
